import { format } from 'date-fns';
import moment from 'moment-timezone';
import { UserFeedbackSurvey } from '../../models/userFeedback';
import { PmStatus } from '../../api/types';

export const getUserPhase = (currentPhase) => {
  switch (currentPhase) {
    case 'PhaseOne':
      return 1;
    case 'PhaseTwo':
      return 2;
    case 'PhaseThree':
      return 3;
    case 'PhaseFour':
      return 4;
    case 'ProgramComplete':
      return 5;
    default:
      return 0;
  }
};

// phase completed is one behind the current phase
// when current phase is 0, we default to 0 instead of returning -1
export const getCompletedPhaseNumber = (currentPhase) => {
  const userPhase = getUserPhase(currentPhase);
  return userPhase > 0 ? userPhase - 1 : 0;
};

export const generateDateStringByTimestamp = (timestamp) => {
  const dateObj = timestamp ? new Date(timestamp) : null;
  return dateObj ? format(dateObj, 'MM-dd-yyyy') : null;
};

const sortByMeetingDateAsc = <T extends { meeting_date: string }>(a: T, b: T) =>
  new Date(a.meeting_date).getTime() - new Date(b.meeting_date).getTime();

// Incomplete meetings (!pm_status): soonest future → nextUp; past/started → toBeCompleted
export const computeMeetingsToDisplay = <
  T extends { meeting_date: string; pm_status?: PmStatus }
>(
  meetings: T[]
): { nextUp: T | undefined; toBeCompleted: T[] } => {
  const now = new Date();

  const incomplete = meetings.filter((meeting) => !meeting.pm_status);

  const toBeCompleted = incomplete
    .filter((meeting) => new Date(meeting.meeting_date) <= now)
    .sort(sortByMeetingDateAsc);

  const nextUp = incomplete
    .filter((meeting) => new Date(meeting.meeting_date) > now)
    .sort(sortByMeetingDateAsc)[0];

  return { nextUp, toBeCompleted };
};

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

/** Join stays available until 2 hours after the meeting start time. */
export const isMeetingJoinable = (meetingDate: string) =>
  Date.now() - new Date(meetingDate).getTime() < TWO_HOURS_MS;

/** Join is enabled only within 24 hours of the meeting start time. */
export const isWithin24HoursOfMeetingStart = (meetingDate: string) =>
  Math.abs(new Date(meetingDate).getTime() - Date.now()) <= TWENTY_FOUR_HOURS_MS;

export const formatDate = (
  isoDateStr: string | undefined,
  // default formatting if not provided,
  // z stands for the timezone abbreviation
  formatPattern: string = 'MMM D, h:mm A z'
): string => {
  if (!isoDateStr) return '--';

  return moment.parseZone(isoDateStr).format(formatPattern);
};

export const formatDateInTZ = (
  isoDateStr: string | undefined,
  timezone: string | undefined = 'America/New_York',
  formatPattern: string = 'dddd, MMMM D, h:mm A z'
) => {
  if (!isoDateStr) return '--';

  return moment.parseZone(isoDateStr).tz(timezone)?.format(formatPattern);
};

export const formatDateInBrowserTZ = (
  isoDateStr: string | undefined,
  // default formatting if not provided,
  // z stands for the timezone abbreviation
  formatPattern: string = 'MMM D, h:mm A z'
): string => {
  if (!isoDateStr) return '--';

  // Automatically get browser's TZ
  const browserTimeZone = moment.tz.guess();
  const localTime = moment.tz(isoDateStr, browserTimeZone);

  return localTime.format(formatPattern);
};

export const getMeetingGoalsByMeetingType = (
  surveys: UserFeedbackSurvey[],
  meetingType: string
) => {
  return surveys.find((s) => s.survey?.appointment_type === meetingType)?.survey
    ?.goals;
};
